import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, CalendarDays, Trash2, Send, CheckCircle2, XCircle, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_COLORS = {
  national: "bg-rose-100 text-rose-800",
  regional: "bg-amber-100 text-amber-800",
  optional: "bg-sky-100 text-sky-800",
  general:  "bg-gray-100 text-gray-700",
};

export default function Holidays() {
  const { user } = useAuth();
  const isHR = ["hr", "super_admin"].includes(user?.role);
  const isApprover = ["manager", "super_admin"].includes(user?.role);
  const isManagerial = isHR || isApprover;

  const [active, setActive] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [refresh, setRefresh] = useState(0);

  // HR upload state
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([{ _rid: crypto.randomUUID(), date: "", name: "", type: "national" }]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submittingForApproval, setSubmittingForApproval] = useState(false);

  // Approver state
  const [reviewing, setReviewing] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    api.get(`/holidays/active?year=${year}`).then((r) => setActive(r.data || null)).catch(() => setActive(null));
    if (isManagerial) {
      api.get("/holidays/calendars").then((r) => setCalendars(r.data || [])).catch(() => setCalendars([]));
    }
  }, [year, refresh, isManagerial]);

  const addRow = () => setRows([...rows, { _rid: crypto.randomUUID(), date: "", name: "", type: "national" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const editRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const handleCSV = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      // Expected format: date,name,type (one per row), with optional header
      const lines = text.split(/\r?\n/).filter(Boolean);
      const parsed = [];
      for (const line of lines) {
        const [d, n, t] = line.split(",").map((s) => (s || "").trim());
        if (!d || !n) continue;
        if (d.toLowerCase() === "date") continue;  // skip header
        parsed.push({ date: d, name: n, type: (t || "national").toLowerCase() });
      }
      if (parsed.length === 0) { toast.error("No holidays found in CSV"); return; }
      setRows(parsed);
      toast.success(`Loaded ${parsed.length} holidays from CSV`);
    } catch (err) {
      toast.error("Failed to read CSV");
    } finally {
      e.target.value = "";
    }
  };

  const saveDraft = async (submit = false) => {
    const valid = rows.filter((r) => r.date && r.name);
    if (valid.length === 0) { toast.error("Add at least one holiday"); return; }
    const setter = submit ? setSubmittingForApproval : setSavingDraft;
    setter(true);
    try {
      const { data: created } = await api.post("/holidays/calendar", { year: Number(year), holidays: valid });
      if (submit) {
        await api.post(`/holidays/calendar/${created.id}/submit`);
        toast.success("Submitted for approval");
      } else {
        toast.success("Draft saved");
      }
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setter(false); }
  };

  const approveCalendar = async (rec) => {
    setActing(true);
    try {
      await api.post(`/holidays/calendar/${rec.id}/approve`, { comment: "" });
      toast.success(`Calendar for ${rec.year} approved`);
      setReviewing(null);
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Approve failed");
    } finally { setActing(false); }
  };

  const rejectCalendar = async () => {
    if (rejectReason.trim().length < 3) { toast.error("Reason required"); return; }
    setActing(true);
    try {
      await api.post(`/holidays/calendar/${reviewing.id}/reject`, { reason: rejectReason });
      toast.success("Sent back to HR");
      setReviewing(null);
      setRejecting(false);
      setRejectReason("");
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reject failed");
    } finally { setActing(false); }
  };

  const pendingForApproval = calendars.filter((c) => c.status === "pending_approval");

  return (
    <div data-testid="holidays-page">
      <PageHeader
        title="Holiday Calendar"
        subtitle={
          isHR ? "Upload the company-wide holiday calendar and submit it for Manager / Admin approval."
          : isApprover ? "Review and approve holiday calendars submitted by HR."
          : "View this year's approved company holidays."
        }
      />

      <Tabs defaultValue="view">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger value="view" data-testid="tab-view" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Active calendar</TabsTrigger>
          {isHR && (
            <TabsTrigger value="upload" data-testid="tab-upload" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Upload calendar</TabsTrigger>
          )}
          {isApprover && (
            <TabsTrigger value="approve" data-testid="tab-approve" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">
              Pending approvals {pendingForApproval.length > 0 ? <span className="ml-1.5 text-[10px] bg-[#F26B3A] text-white rounded-full px-1.5">{pendingForApproval.length}</span> : null}
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── ACTIVE (read-only for everyone) ─────────────────────────── */}
        <TabsContent value="view">
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Year</Label>
            <Input data-testid="holiday-year" type="number" className="w-24" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </div>

          {active && active.status === "approved" ? (
            <Card className="hr-card p-6" data-testid="active-calendar">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="font-display text-lg font-semibold text-[#0F172A]">{active.holidays.length} holidays for {active.year}</div>
                  <div className="text-xs text-gray-500">Approved by {active.approved_by} · {(active.approved_at || "").slice(0,10)}</div>
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Approved</span>
              </div>
              <HolidayTable holidays={active.holidays} />
            </Card>
          ) : (
            <Card className="hr-card p-10 text-center text-sm text-gray-500">
              No approved holiday calendar for {year} yet.
              {isHR && <div className="mt-2">Go to <b>Upload calendar</b> to publish one.</div>}
            </Card>
          )}
        </TabsContent>

        {/* ── HR UPLOAD ───────────────────────────────────────────────── */}
        {isHR && (
        <TabsContent value="upload">
          <Card className="hr-card p-6" data-testid="upload-calendar">
            <div className="flex flex-wrap items-end gap-3 mb-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Year</Label>
                <Input data-testid="upload-year" type="number" className="w-28 mt-1.5" value={year} onChange={(e) => setYear(Number(e.target.value))} />
              </div>
              <label className="block">
                <input type="file" accept=".csv,text/csv" onChange={handleCSV} className="hidden" data-testid="csv-input" />
                <Button asChild variant="outline" className="cursor-pointer h-9">
                  <span><Upload className="h-4 w-4 mr-1.5" /> Import from CSV</span>
                </Button>
              </label>
              <span className="text-[11px] text-gray-500">CSV format: <code>date,name,type</code> · type: national / regional / optional</span>
            </div>

            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={r._rid} className="grid grid-cols-12 gap-2 items-center" data-testid={`holiday-row-${i}`}>
                  <Input data-testid={`holiday-date-${i}`} type="date" className="col-span-3" value={r.date} onChange={(e) => editRow(i, { date: e.target.value })} />
                  <Input data-testid={`holiday-name-${i}`} className="col-span-6" placeholder="Republic Day" value={r.name} onChange={(e) => editRow(i, { name: e.target.value })} />
                  <Select value={r.type} onValueChange={(v) => editRow(i, { type: v })}>
                    <SelectTrigger data-testid={`holiday-type-${i}`} className="col-span-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="national">National</SelectItem>
                      <SelectItem value="regional">Regional</SelectItem>
                      <SelectItem value="optional">Optional</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid={`holiday-remove-${i}`} size="icon" variant="ghost" className="col-span-1 text-rose-600" onClick={() => removeRow(i)} disabled={rows.length === 1}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button data-testid="add-holiday-row" variant="outline" className="mt-3 h-9" onClick={addRow}>
              <Plus className="h-4 w-4 mr-1.5" /> Add holiday
            </Button>

            <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100">
              <Button data-testid="save-draft" variant="outline" onClick={() => saveDraft(false)} disabled={savingDraft || submittingForApproval}>
                {savingDraft ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Save draft
              </Button>
              <Button data-testid="submit-for-approval" onClick={() => saveDraft(true)} disabled={savingDraft || submittingForApproval} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
                {submittingForApproval ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                Submit for approval
              </Button>
            </div>

            <div className="mt-4 text-[11px] text-gray-500">
              Note: submitting will overwrite any existing draft for the same year. Once Manager / Admin approves, employees can see the calendar.
            </div>
          </Card>
        </TabsContent>
        )}

        {/* ── MANAGER/ADMIN APPROVAL ──────────────────────────────────── */}
        {isApprover && (
        <TabsContent value="approve">
          <div className="space-y-3">
            {pendingForApproval.length === 0 ? (
              <Card className="hr-card p-10 text-center text-sm text-gray-500">No calendars awaiting approval.</Card>
            ) : pendingForApproval.map((c) => (
              <Card key={c.id} className="hr-card p-5 cursor-pointer hover:shadow-md transition-shadow" data-testid={`pending-${c.id}`} onClick={() => setReviewing(c)}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display font-semibold text-[#0F172A]">Year {c.year} · {c.holidays.length} holidays</div>
                    <div className="text-[11px] text-gray-500">Submitted by {c.uploaded_by} on {(c.submitted_at || c.uploaded_at || "").slice(0,10)}</div>
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Pending</span>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        )}
      </Tabs>

      {/* Review dialog for approvers */}
      <Dialog open={!!reviewing} onOpenChange={(v) => { if (!v) { setReviewing(null); setRejecting(false); setRejectReason(""); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-calendar-dialog">
          <DialogHeader>
            <DialogTitle>Review holiday calendar — {reviewing?.year}</DialogTitle>
          </DialogHeader>
          {reviewing && (
            <div className="space-y-4">
              <div className="text-xs text-gray-500">
                Submitted by <b>{reviewing.uploaded_by}</b> · {reviewing.holidays.length} holidays
              </div>
              <HolidayTable holidays={reviewing.holidays} />
              {rejecting && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Rejection reason</Label>
                  <Textarea rows={3} data-testid="reject-reason" className="mt-1.5" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Missing Diwali holiday" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!rejecting ? (
              <>
                <Button variant="outline" data-testid="open-reject" onClick={() => setRejecting(true)}>
                  <XCircle className="h-4 w-4 mr-1.5 text-rose-600" /> Send back
                </Button>
                <Button data-testid="approve-calendar" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approveCalendar(reviewing)} disabled={acting}>
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</>}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setRejecting(false); setRejectReason(""); }}>Cancel</Button>
                <Button data-testid="confirm-reject" className="bg-rose-600 hover:bg-rose-700" onClick={rejectCalendar} disabled={acting}>
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send back"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HolidayTable({ holidays }) {
  const sorted = [...(holidays || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return (
    <div className="border border-gray-100 rounded-md overflow-hidden">
      <div className="grid grid-cols-12 bg-gray-50 px-4 py-2 text-[10px] uppercase tracking-wider text-gray-500">
        <div className="col-span-3">Date</div>
        <div className="col-span-6">Holiday</div>
        <div className="col-span-3">Type</div>
      </div>
      {sorted.map((h, i) => {
        const d = new Date(h.date);
        const day = isNaN(d) ? "" : d.toLocaleDateString("en-US", { weekday: "short" });
        return (
          <div key={`${h.date}-${i}`} className="grid grid-cols-12 px-4 py-2 border-t border-gray-100 text-sm" data-testid={`holiday-${h.date}`}>
            <div className="col-span-3"><span className="font-mono">{h.date}</span> {day && <span className="text-[11px] text-gray-400 ml-1">{day}</span>}</div>
            <div className="col-span-6 text-gray-800">{h.name}</div>
            <div className="col-span-3"><span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${TYPE_COLORS[h.type] || TYPE_COLORS.general}`}>{h.type || "general"}</span></div>
          </div>
        );
      })}
      {sorted.length === 0 && <div className="px-4 py-6 text-center text-xs text-gray-500">No holidays.</div>}
    </div>
  );
}
